import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, getAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getPalette } from "@/lib/palette";
import { Plus, MapPin, X, Edit2, Camera, Trash2, Clock, ChevronDown, ChevronUp, Download, Upload, Euro } from "lucide-react";

const STATUS_LABELS = {
  offerte: "Offerte",
  gepland: "Gepland",
  in_uitvoering: "In uitvoering",
  opgeleverd: "Opgeleverd",
  afgerond: "Afgerond",
};

export default function Projecten() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(null); // null | "new" | project
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notionModal, setNotionModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportNotion = async () => {
    if (!projects.length) return;
    if (!window.confirm(`${projects.length} project(en) naar Notion exporteren?`)) return;
    setExporting(true);
    try {
      const { data } = await api.post("/integrations/notion/export", { project_ids: projects.map((p) => p.id) });
      toast.success(`Notion: ${data.created} nieuw, ${data.updated} bijgewerkt`);
      if (data.errors?.length) toast.error(`${data.errors.length} fout(en): ${data.errors[0]}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export naar Notion mislukt"); }
    finally { setExporting(false); }
  };
  const office = ["OWNER", "PLANNER"].includes(user?.role);
  const isOwner = user?.role === "OWNER";

  // Open Notion-import vanuit Planbord-rechtsklik: /projecten?notion=1
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("notion") === "1" && office) {
      setNotionModal(true);
      url.searchParams.delete("notion");
      window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
    }
  }, [office]);

  const load = async () => setProjects((await api.get("/projects")).data);
  useEffect(() => { load(); }, []);

  // Group projects by month (based on created_at). Newest month first.
  const projectsByMonth = React.useMemo(() => {
    const NL_MONTHS = ["Januari","Februari","Maart","April","Mei","Juni","Juli","Augustus","September","Oktober","November","December"];
    const groups = {};
    for (const p of projects) {
      const d = p.created_at ? new Date(p.created_at) : new Date();
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = `${NL_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      if (!groups[key]) groups[key] = { label, items: [] };
      groups[key].items.push(p);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, v]) => ({ key, label: v.label, items: v.items }));
  }, [projects]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/projects/${confirmDelete.id}`);
      toast.success("Project verwijderd");
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kon niet verwijderen"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
        <h1 className="pb-heading text-3xl font-black">PROJECTEN</h1>
        {office && (
          <div className="flex gap-2 flex-wrap">
            <button className="pb-btn-outline flex items-center gap-2" onClick={() => setNotionModal(true)} data-testid="btn-notion-import">
              <Download size={16}/> Vanuit Notion
            </button>
            <button className="pb-btn-outline flex items-center gap-2 disabled:opacity-60" onClick={exportNotion} disabled={exporting} data-testid="btn-notion-export">
              <Upload size={16}/> {exporting ? "Exporteren…" : "Naar Notion"}
            </button>
            <button className="pb-btn-primary flex items-center gap-2" onClick={() => setModal("new")} data-testid="btn-new-project">
              <Plus size={18}/> Nieuw project
            </button>
          </div>
        )}
      </div>
      <div className="space-y-8">
        {projectsByMonth.length === 0 && (
          <div className="text-center py-16 text-slate-500 font-semibold">Nog geen projecten. Klik op "Nieuw project" om te beginnen.</div>
        )}
        {projectsByMonth.map((group) => (
          <div key={group.key} data-testid={`project-group-${group.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="pb-heading text-lg font-black uppercase tracking-widest text-slate-700">{group.label}</h2>
              <div className="flex-1 h-0.5 bg-slate-200"/>
              <span className="text-xs font-bold text-slate-500">{group.items.length} project{group.items.length === 1 ? "" : "en"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.items.map((p) => (
          <div key={p.id} className="pb-card overflow-hidden relative" data-testid={`project-card-${p.id}`}>
            <Link to={`/projecten/${p.id}`} className="block hover:opacity-95">
              <div className="h-3" style={{ background: p.color || "#3B82F6" }}/>
              <div className="p-4 pt-4">
                <div className="flex items-start gap-2 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{p.project_number}</div>
                    <div className="pb-heading text-xl font-black mt-1 pr-24">{p.name}</div>
                  </div>
                </div>
                <div className="mt-1">
                  <span className="inline-block text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded text-white" style={{ background: getStatusColor(p.status) }}>{STATUS_LABELS[p.status]}</span>
                </div>
                <div className="text-sm font-semibold text-slate-700 mt-2">{p.customer_name}</div>
                <div className="mt-2 flex items-center gap-1 text-sm text-slate-600 font-semibold">
                  <MapPin size={14}/> {[p.address, p.city].filter(Boolean).join(", ") || "Geen adres"}
                </div>
              </div>
            </Link>
            {office && <ProjectHoursDropdown projectId={p.id} />}
            {office && (
              <button
                onClick={(e) => { e.preventDefault(); setModal(p); }}
                className="absolute top-6 right-3 p-2 bg-white border-2 border-slate-900 rounded hover:bg-orange-50 shadow-sm"
                data-testid={`btn-edit-project-${p.id}`}
                aria-label="Bewerken"
              >
                <Edit2 size={14}/>
              </button>
            )}
            {isOwner && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(p); }}
                className="absolute top-6 right-14 p-2 bg-white border-2 border-red-600 text-red-600 rounded hover:bg-red-50 shadow-sm"
                data-testid={`btn-delete-project-${p.id}`}
                aria-label="Verwijderen"
              >
                <Trash2 size={14}/>
              </button>
            )}
          </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {modal && <ProjectModal existing={modal === "new" ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {notionModal && <NotionImportModal onClose={() => setNotionModal(false)} onDone={() => { setNotionModal(false); load(); }} />}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60">
          <div className="pb-card w-full max-w-sm p-5" data-testid="confirm-delete-project">
            <div className="pb-heading text-lg font-black mb-2">Project verwijderen?</div>
            <div className="text-sm text-slate-700 font-medium mb-4">
              &ldquo;{confirmDelete.name}&rdquo; wordt permanent verwijderd. Gekoppelde uren blijven behouden, maar bijbehorende planningen en <b>kostenregels</b> verdwijnen.
            </div>
            <div className="rounded border-2 border-amber-500 bg-amber-50 p-3 mb-4 text-xs font-semibold text-amber-900" data-testid="confirm-delete-export-hint">
              Exporteer eerst de kosten van dit project, anders ben je ze kwijt.
              <button type="button" onClick={() => downloadCostReport(confirmDelete.id, confirmDelete.name)} className="pb-btn-outline !min-h-[36px] w-full mt-2 flex items-center justify-center gap-1" data-testid="confirm-delete-export-btn">
                <Download size={14}/> Kosten exporteren (Excel)
              </button>
            </div>
            <div className="flex gap-2">
              <button className="pb-btn-outline flex-1" onClick={() => setConfirmDelete(null)}>Annuleren</button>
              <button className="pb-btn-primary !bg-red-600 flex-1" onClick={doDelete} data-testid="confirm-delete-project-ok">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function getStatusColor(s) {
  switch (s) {
    case "gepland": return "#64748B";
    case "in_uitvoering": return "#F97316";
    case "opgeleverd": return "#3B82F6";
    case "afgerond": return "#16A34A";
    default: return "#94A3B8";
  }
}

function fmtHM(min) {
  const m = min || 0;
  return `${Math.floor(m / 60)}u ${String(m % 60).padStart(2, "0")}m`;
}

export function ProjectHoursDropdown({ projectId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/hours`);
      setData(r.data);
    } catch { toast.error("Kon uren niet laden"); }
    finally { setLoading(false); }
  };

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && !data) load();
    setOpen((v) => !v);
  };

  return (
    <div className="border-t-2 border-slate-900" data-testid={`project-hours-${projectId}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-orange-50 text-left"
        data-testid={`project-hours-toggle-${projectId}`}
      >
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-orange-600"/>
          <span className="text-xs font-black uppercase tracking-widest text-slate-700">Uren</span>
          {data && (
            <span className="text-xs font-bold tabular-nums text-slate-900 ml-1">
              {fmtHM(data.total_minutes)} · {data.users.length} pers.
            </span>
          )}
        </div>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>
      {open && (
        <div className="px-4 pb-3 bg-slate-50" data-testid={`project-hours-panel-${projectId}`}>
          {loading && <div className="text-xs text-slate-500 py-2">Laden…</div>}
          {!loading && data && (
            <>
              <div className="flex items-center justify-between py-2 border-b-2 border-slate-900">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-600">Totaal op dit project</span>
                <span className="pb-heading font-black tabular-nums text-lg text-orange-600" data-testid={`project-hours-total-${projectId}`}>{fmtHM(data.total_minutes)}</span>
              </div>
              {data.users.length === 0 && (
                <div className="text-xs text-slate-500 py-2">Nog geen uren geregistreerd op dit project.</div>
              )}
              <div className="divide-y divide-slate-200">
                {data.users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between py-2" data-testid={`project-hours-user-${projectId}-${u.user_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold truncate">{u.user_name}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{u.function || "Uitvoerder"} · {u.entries} shift{u.entries === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-sm font-black tabular-nums">{fmtHM(u.minutes)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ProjectModal({ existing, onClose, onSaved }) {
  const { company } = useAuth();
  const palette = getPalette(company);
  const editing = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    customer_name: existing?.customer_name || "",
    contact_person: existing?.contact_person || "",
    contact_phone: existing?.contact_phone || "",
    address: existing?.address || "",
    postcode: existing?.postcode || "",
    house_number: existing?.house_number || "",
    city: existing?.city || "",
    project_number: existing?.project_number || "",
    status: existing?.status || "gepland",
    color: existing?.color || "#3B82F6",
    budget_hours: existing?.budget_hours ?? 0,
    budget: existing?.budget ?? 0,
    description: existing?.description || "",
    parking_info: existing?.parking_info || "",
    access_info: existing?.access_info || "",
    photos: existing?.photos || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Ref-based file input trigger. Nested <label>-elementen (bijv. binnen Fld)
  // vangen op sommige browsers de klik af en "selecteren de tekst" i.p.v. de
  // bestandskiezer te openen. Door de input via een ref te .click()'en werkt
  // het gegarandeerd op elke browser + mobiel.
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!existing?.id || existing.photos) return;
    api.get(`/projects/${existing.id}`).then((r) => setForm((f) => ({ ...f, photos: r.data.photos || [] }))).catch(() => {});
  }, [existing]);

  const compressImage = (file, maxW = 1600, quality = 0.7) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.photos.length + files.length > 10) {
      toast.error("Maximaal 10 foto's per project");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const compressed = await Promise.all(files.map((f) => compressImage(f)));
      const existingSet = new Set(form.photos);
      const uniques = compressed.filter((src) => !existingSet.has(src));
      if (existing?.id) {
        // Edit-mode: upload elke foto los naar de backend. Elke request bevat
        // maar één base64 (~200-400KB) waardoor we ruim onder de ingress
        // body-limit blijven — óók als er al 5-9 foto's staan.
        let latest = form.photos;
        for (const src of uniques) {
          try {
            await api.post(`/projects/${existing.id}/photos`, { data_url: src });
            latest = [...latest, src];
          } catch (err) {
            const msg = err?.response?.data?.detail || "Foto uploaden mislukt";
            toast.error(msg);
            break;
          }
        }
        setForm((f) => ({ ...f, photos: latest.slice(0, 10) }));
      } else {
        // Create-mode: buffer lokaal; na aanmaken wordt elke foto los geüpload.
        setForm((f) => ({ ...f, photos: [...f.photos, ...uniques].slice(0, 10) }));
      }
    } catch { toast.error("Kon foto niet verwerken. Gebruik een JPG- of PNG-foto."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = async (i) => {
    if (existing?.id && form.photos[i]) {
      try {
        await api.delete(`/projects/${existing.id}/photos/${i}`);
      } catch { toast.error("Foto verwijderen mislukt"); return; }
    }
    setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { photos, ...rest } = form;
      const payload = { ...rest, budget_hours: Number(form.budget_hours) || 0, budget: Number(form.budget) || 0 };
      const addressChanged = editing && (
        (existing.address || "") !== form.address ||
        (existing.house_number || "") !== form.house_number ||
        (existing.city || "") !== form.city
      );
      if ((!editing || addressChanged) && form.address && form.city) {
        try {
          const q = encodeURIComponent(`${form.address} ${form.house_number}, ${form.city}, Nederland`);
          const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`);
          const d = await r.json();
          if (d[0]) { payload.latitude = parseFloat(d[0].lat); payload.longitude = parseFloat(d[0].lon); }
          else if (addressChanged) { payload.latitude = null; payload.longitude = null; }
        } catch (_) { /* non-fatal */ }
      }
      if (editing) {
        await api.patch(`/projects/${existing.id}`, payload);
        toast.success("Project bijgewerkt");
      } else {
        const { data: created } = await api.post("/projects", payload);
        let failed = 0;
        for (const src of photos) {
          try { await api.post(`/projects/${created.id}/photos`, { data_url: src }); } catch { failed += 1; }
        }
        if (failed) toast.error(`${failed} foto('s) konden niet worden opgeslagen`);
        toast.success("Project aangemaakt");
      }
      onSaved();
    } catch (e) {
      toast.error(editing ? "Kon project niet opslaan" : "Kon project niet aanmaken");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-900/50">
      <form onSubmit={submit} className="pb-card w-full max-w-2xl max-h-[95vh] overflow-y-auto pb-scrollbar" data-testid="project-modal">
        <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center justify-between">
          <div className="pb-heading text-xl font-black">{editing ? "Project bewerken" : "Nieuw project"}</div>
          <button type="button" onClick={onClose} className="text-white"><X size={22}/></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld label="Projectnaam" required><input className="pb-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="np-name"/></Fld>
          <Fld label="Projectnummer"><input className="pb-input" value={form.project_number} onChange={(e)=>setForm({...form, project_number: e.target.value})} data-testid="np-project-number"/></Fld>
          <Fld label="Klantnaam"><input className="pb-input" value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})}/></Fld>
          <Fld label="Contactpersoon"><input className="pb-input" value={form.contact_person} onChange={(e)=>setForm({...form, contact_person: e.target.value})}/></Fld>
          <Fld label="Telefoon"><input className="pb-input" value={form.contact_phone} onChange={(e)=>setForm({...form, contact_phone: e.target.value})}/></Fld>
          <Fld label="Status">
            <select className="pb-input" value={form.status} onChange={(e)=>setForm({...form, status: e.target.value})} data-testid="np-status">
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Fld>
          <Fld label="Straat"><input className="pb-input" value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} data-testid="np-address"/></Fld>
          <Fld label="Huisnr"><input className="pb-input" value={form.house_number} onChange={(e)=>setForm({...form, house_number: e.target.value})} data-testid="np-house-number"/></Fld>
          <Fld label="Postcode"><input className="pb-input" value={form.postcode} onChange={(e)=>setForm({...form, postcode: e.target.value})}/></Fld>
          <Fld label="Plaats"><input className="pb-input" value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid="np-city"/></Fld>
          <Fld label="Kleur (planbord)">
            <div className="space-y-2">
              <input type="color" className="pb-input" value={form.color} onChange={(e)=>setForm({...form, color: e.target.value})} data-testid="np-color"/>
              <div className="grid grid-cols-2 gap-1.5" data-testid="np-palette">
                {palette.map((p) => {
                  const active = form.color?.toLowerCase() === p.color.toLowerCase();
                  return (
                    <button
                      key={p.color}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, color: p.color }))}
                      className={`flex items-center gap-2 px-2 py-1 rounded border-2 text-xs font-bold text-left transition-colors ${active ? "border-slate-900 bg-orange-50 ring-2 ring-orange-500" : "border-slate-200 hover:border-slate-900"}`}
                      data-testid={`np-palette-${p.color.replace('#','')}`}
                    >
                      <span className="w-5 h-5 rounded border border-slate-900 shrink-0" style={{ background: p.color }}/>
                      <span className="truncate">{p.name || p.color}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Fld>
          <Fld label="Begrote uren"><input type="number" className="pb-input" value={form.budget_hours} onChange={(e)=>setForm({...form, budget_hours: e.target.value})} data-testid="np-budget-hours"/></Fld>
          <Fld label="Budget (€)"><input type="number" step="0.01" className="pb-input" value={form.budget} onChange={(e)=>setForm({...form, budget: e.target.value})} data-testid="np-budget" placeholder="0.00"/></Fld>
          <div className="md:col-span-2">
            <Fld label="Omschrijving"><textarea className="pb-input min-h-[80px] py-2" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}/></Fld>
          </div>
          <div className="md:col-span-2"><Fld label="Aanrijinstructies"><textarea className="pb-input min-h-[60px] py-2" value={form.parking_info} onChange={(e)=>setForm({...form, parking_info: e.target.value})}/></Fld></div>
          <div className="md:col-span-2"><Fld label="Toegangsinfo"><textarea className="pb-input min-h-[60px] py-2" value={form.access_info} onChange={(e)=>setForm({...form, access_info: e.target.value})}/></Fld></div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">Foto&apos;s van het project ({form.photos.length}/10)</div>
            <div className="mt-1 space-y-2">
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {form.photos.map((src, i) => (
                    <div key={`${i}-${src.length}-${src.slice(-40)}`} className="relative group border-2 border-slate-900 rounded overflow-hidden" data-testid={`project-photo-preview-${i}`}>
                      <img src={src} alt="" className="w-full h-24 object-cover"/>
                      <button type="button" onClick={()=>removePhoto(i)} className="absolute top-1 right-1 bg-white/95 border-2 border-slate-900 rounded p-1" data-testid={`project-photo-remove-${i}`}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {form.photos.length < 10 && (
                <>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                    className="pb-btn-outline w-full flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="project-photo-input-label"
                  >
                    <Camera size={16}/> {uploading ? "Verwerken…" : "Foto's toevoegen"}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onPickPhotos}
                    disabled={uploading}
                    data-testid="project-photo-input"
                  />
                </>
              )}
            </div>
          </div>
          {existing?.id && <ProjectCostsSection projectId={existing.id} projectName={existing.name} />}
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2">
          <button type="button" onClick={onClose} className="pb-btn-outline flex-1" data-testid="np-cancel">Annuleren</button>
          <button type="submit" disabled={saving} className="pb-btn-primary flex-1" data-testid="np-save">{saving ? "Opslaan…" : (editing ? "Opslaan" : "Aanmaken")}</button>
        </div>
      </form>
    </div>
  );
}

export async function downloadCostReport(projectId, name = "project") {
  try {
    const token = getAuthToken();
    const resp = await fetch(`${API_BASE}/projects/${projectId}/cost-report.xlsx`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error("export failed");
    const url = URL.createObjectURL(await resp.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `projectkosten-${name.replace(/\s+/g, "-")}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Kostenoverzicht gedownload");
    return true;
  } catch { toast.error("Export mislukt"); return false; }
}

export function ProjectCostsSection({ projectId, projectName }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState("materiaal");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/costs`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) { console.warn("Kosten laden mislukt", err); }
  };
  useEffect(() => { load(); }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addCost = async () => {
    const a = parseFloat(amount);
    if (!a || a < 0) { toast.error("Voer een geldig bedrag in"); return; }
    setAdding(true);
    try {
      await api.post(`/projects/${projectId}/costs`, {
        kind, description: desc, amount: a,
      });
      setDesc(""); setAmount(""); setKind("materiaal");
      await load();
      toast.success("Kostenregel toegevoegd", { description: "Exporteer de kosten per project, anders raak je ze kwijt als het project wordt verwijderd." });
    } catch { toast.error("Kon niet toevoegen"); }
    finally { setAdding(false); }
  };

  const delCost = async (cid) => {
    if (!window.confirm("Kostenregel verwijderen?")) return;
    try { await api.delete(`/projects/${projectId}/costs/${cid}`); await load(); }
    catch { toast.error("Kon niet verwijderen"); }
  };

  const KIND_LABEL = { materiaal: "Materiaal", onderaannemer: "Onderaannemer", overige: "Overige" };
  const fmtEur = (v) => (v || 0).toLocaleString("nl-NL", { style: "currency", currency: "EUR" });

  return (
    <div className="md:col-span-2 mt-2 pt-4 border-t-2 border-slate-200" data-testid="project-costs-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Euro size={18} className="text-orange-600"/>
          <h3 className="pb-heading text-lg font-black">Kostenregels</h3>
        </div>
        <div className="text-sm font-bold text-slate-700" data-testid="costs-total">
          Totaal: <span className="text-orange-700">{fmtEur(total)}</span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between rounded border-2 border-amber-500 bg-amber-50 p-3 mb-3" data-testid="costs-export-hint">
        <div className="text-xs font-semibold text-amber-900">
          Exporteer de kosten per project (personeel: uren × uurtarief + materiaal + totaal). Wordt het project verwijderd, dan zijn de kosten weg.
        </div>
        <button type="button" onClick={() => downloadCostReport(projectId, projectName)} className="pb-btn-outline !min-h-[36px] flex items-center gap-1 shrink-0" data-testid="costs-export-btn">
          <Download size={14}/> Exporteer (Excel)
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <select className="pb-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cost-kind">
          <option value="materiaal">Materiaal</option>
          <option value="onderaannemer">Onderaannemer</option>
          <option value="overige">Overige</option>
        </select>
        <input className="pb-input md:col-span-2" placeholder="Omschrijving" value={desc} onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCost(); } }} data-testid="cost-desc"/>
        <div className="flex gap-2">
          <input type="number" step="0.01" className="pb-input flex-1" placeholder="€ 0,00" value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCost(); } }} data-testid="cost-amount"/>
          <button type="button" onClick={addCost} className="pb-btn-primary !px-3" disabled={adding} data-testid="cost-add"><Plus size={16}/></button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 italic text-center py-4">Nog geen kosten toegevoegd.</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((it) => (
            <li key={it.id} className="py-2 flex items-center justify-between gap-2" data-testid={`cost-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold truncate">{it.description || "—"}</div>
                <div className="text-xs text-slate-500 font-medium">{KIND_LABEL[it.kind] || it.kind} · {it.date}</div>
              </div>
              <div className="font-black text-slate-900 whitespace-nowrap">{fmtEur(it.amount)}</div>
              <button type="button" onClick={() => delCost(it.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`cost-del-${it.id}`}>
                <Trash2 size={14}/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotionImportModal({ onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [notConnected, setNotConnected] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/integrations/notion/preview");
        setItems(r.data.items || []);
      } catch (e) {
        if (e?.response?.data?.detail?.includes("niet gekoppeld")) {
          setNotConnected(true);
        } else {
          toast.error(e?.response?.data?.detail || "Kon Notion niet laden");
        }
      }
      setLoading(false);
    })();
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const doImport = async () => {
    if (selected.size === 0) { toast.error("Kies minstens 1 project"); return; }
    setImporting(true);
    try {
      const r = await api.post("/integrations/notion/import", { notion_ids: Array.from(selected) });
      toast.success(`${r.data.created} geïmporteerd · ${r.data.skipped} overgeslagen`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} fout(en) opgetreden`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import mislukt"); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-900/50">
      <div className="pb-card w-full max-w-2xl max-h-[90vh] overflow-y-auto pb-scrollbar" data-testid="notion-import-modal">
        <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">Notion</div>
            <div className="pb-heading text-xl font-black">Projecten importeren</div>
          </div>
          <button type="button" onClick={onClose} className="text-white" data-testid="notion-import-close"><X size={22}/></button>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Laden uit Notion…</div>
          ) : notConnected ? (
            <div className="text-center py-6">
              <div className="font-bold text-slate-800 mb-2">Notion is nog niet gekoppeld.</div>
              <a href="/instellingen" className="pb-btn-primary inline-flex items-center gap-1">Ga naar Instellingen</a>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-6 text-slate-500 italic">Geen items gevonden in de Notion-database.</div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between text-xs text-slate-600 font-medium">
                <span>{items.length} items gevonden · {selected.size} geselecteerd</span>
                <button type="button" onClick={() => setSelected(new Set(items.filter((i) => !i.already_imported).map((i) => i.notion_id)))} className="text-orange-700 hover:underline font-bold" data-testid="notion-select-all-new">
                  Selecteer alle nieuwe
                </button>
              </div>
              <ul className="space-y-1 max-h-[55vh] overflow-y-auto pb-scrollbar">
                {items.map((it) => (
                  <li key={it.notion_id} className={`border-2 rounded p-2 flex items-start gap-3 ${selected.has(it.notion_id) ? "border-orange-500 bg-orange-50" : "border-slate-200"} ${it.already_imported ? "opacity-50" : ""}`} data-testid={`notion-item-${it.notion_id}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(it.notion_id)}
                      disabled={it.already_imported}
                      onChange={() => toggle(it.notion_id)}
                      className="mt-1 w-5 h-5 accent-orange-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-sm border border-slate-900" style={{ background: it.color }}/>
                        <div className="font-black truncate">{it.name || "(zonder naam)"}</div>
                        {it.already_imported && <span className="text-[10px] font-bold uppercase bg-slate-200 px-1.5 py-0.5 rounded">Al in Ploegbord</span>}
                      </div>
                      <div className="text-xs text-slate-600 font-medium truncate">
                        {it.address} {it.postcode} {it.city}
                      </div>
                      {it.budget > 0 && <div className="text-xs text-slate-500">Budget: €{it.budget}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="pb-btn-outline flex-1" data-testid="notion-import-cancel">Annuleren</button>
          <button type="button" onClick={doImport} className="pb-btn-primary flex-1 flex items-center justify-center gap-1" disabled={importing || selected.size === 0 || notConnected} data-testid="notion-import-confirm">
            <Upload size={16}/> {importing ? "Importeren…" : `Importeer (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-700">{label}{required && " *"}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
